import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, ExternalLink, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type Resolver = (v: { ok: boolean; gammaUrl?: string } | null) => void;

interface OpenPayload {
  contentMarkdown: string;
  defaultTitle?: string;
  resolve: Resolver;
}

const EVENT_NAME = "thiia:gamma-export:open";

export interface GammaExportRequest {
  contentMarkdown: string;
  defaultTitle?: string;
}

export function openGammaExport(req: GammaExportRequest): Promise<{ ok: boolean; gammaUrl?: string } | null> {
  return new Promise((resolve) => {
    window.dispatchEvent(new CustomEvent<OpenPayload>(EVENT_NAME, {
      detail: { ...req, resolve },
    }));
  });
}

type Stage = "config" | "generating";

export default function GammaExportHost() {
  const [state, setState] = useState<OpenPayload | null>(null);
  const [stage, setStage] = useState<Stage>("config");
  const [format, setFormat] = useState("presentation");
  const [themeName, setThemeName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [textMode, setTextMode] = useState("preserve");
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [pollMessage, setPollMessage] = useState("Préparation…");
  const [templates, setTemplates] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent<OpenPayload>).detail;
      if (!detail) return;
      // Load user defaults
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data } = await supabase
            .from("gamma_credentials" as any)
            .select("default_format, default_theme, templates")
            .eq("user_id", user.id)
            .maybeSingle();
          const c = data as any;
          if (c?.default_format) setFormat(c.default_format);
          if (c?.default_theme) setThemeName(c.default_theme);
          setTemplates(Array.isArray(c?.templates) ? c.templates.filter(Boolean) : []);
        }
      } catch { /* ignore */ }
      setState(detail);
      setStage("config");
      setInstructions("");
      setGenerationId(null);
    };
    window.addEventListener(EVENT_NAME, handler as EventListener);
    return () => window.removeEventListener(EVENT_NAME, handler as EventListener);
  }, []);

  const close = (v: { ok: boolean; gammaUrl?: string } | null) => {
    cancelledRef.current = true;
    abortRef.current?.abort();
    abortRef.current = null;
    state?.resolve(v);
    setState(null);
  };

  const cancelGeneration = () => {
    cancelledRef.current = true;
    abortRef.current?.abort();
    abortRef.current = null;
    setGenerationId(null);
    setPollMessage("Préparation…");
  };

  const handleBack = () => {
    cancelGeneration();
    setStage("config");
  };

  const startGeneration = async () => {
    if (!state) return;
    cancelledRef.current = false;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStage("generating");
    setPollMessage("Envoi à Gamma.app…");
    try {
      const { data, error } = await supabase.functions.invoke("gamma-generate", {
        body: {
          inputText: state.contentMarkdown,
          format,
          textMode,
          themeName: themeName || undefined,
          additionalInstructions: instructions || undefined,
        },
      });
      if (cancelledRef.current || controller.signal.aborted) return;
      if (error || !data?.generationId) {
        const msg = (error as any)?.message || (data as any)?.error || "Génération impossible";
        toast.error("Gamma.app", { description: msg });
        setStage("config");
        return;
      }
      setGenerationId(data.generationId);
      pollStatus(data.generationId, controller);
    } catch (e: any) {
      if (cancelledRef.current || controller.signal.aborted) return;
      toast.error("Gamma.app", { description: e?.message || "Erreur" });
      setStage("config");
    }
  };

  const pollStatus = async (id: string, controller: AbortController) => {
    const MAX_ATTEMPTS = 60; // 3 minutes at 3s
    let attempts = 0;
    setPollMessage("Gamma prépare votre document…");
    while (attempts < MAX_ATTEMPTS) {
      await new Promise((r) => setTimeout(r, 3000));
      if (cancelledRef.current || controller.signal.aborted) return;
      attempts++;
      try {
        const session = (await supabase.auth.getSession()).data.session;
        const projectId = (import.meta as any).env.VITE_SUPABASE_PROJECT_ID;
        const url = `https://${projectId}.supabase.co/functions/v1/gamma-status?id=${encodeURIComponent(id)}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${session?.access_token || ""}` },
          signal: controller.signal,
        });
        if (cancelledRef.current || controller.signal.aborted) return;
        const json = await res.json();
        if (cancelledRef.current || controller.signal.aborted) return;
        if (!res.ok) {
          toast.error("Gamma.app", { description: json?.error || `Erreur ${res.status}` });
          setStage("config");
          return;
        }
        const status = String(json?.status || "").toLowerCase();
        if (status === "completed" || status === "succeeded" || status === "success") {
          const gammaUrl = json?.gammaUrl;
          if (gammaUrl) {
            window.open(gammaUrl, "_blank", "noopener,noreferrer");
            toast.success("Document Gamma prêt !");
            close({ ok: true, gammaUrl });
          } else {
            toast.error("Gamma.app", { description: "URL manquante dans la réponse." });
            setStage("config");
          }
          return;
        }
        if (status === "failed" || status === "error") {
          toast.error("Gamma.app", { description: json?.raw?.error || "Génération échouée" });
          setStage("config");
          return;
        }
        setPollMessage(`Gamma prépare votre document… (${attempts * 3}s)`);
      } catch (e: any) {
        if (cancelledRef.current || controller.signal.aborted || e?.name === "AbortError") return;
        toast.error("Gamma.app", { description: e?.message || "Erreur de polling" });
        setStage("config");
        return;
      }
    }
    if (cancelledRef.current || controller.signal.aborted) return;
    toast.warning("Gamma.app", { description: "Génération trop longue — vérifiez sur gamma.app." });
    setStage("config");
  };

  if (!state) return null;

  return (
    <Dialog open={!!state} onOpenChange={(o) => { if (!o && stage !== "generating") close(null); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="w-5 h-5 rounded-md bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            Générer avec Gamma.app
          </DialogTitle>
          <DialogDescription>
            Créez une présentation, un document ou un post visuel à partir de ce contenu.
          </DialogDescription>
        </DialogHeader>

        {stage === "config" && (
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Format</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presentation">Présentation</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="social">Post social</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Mode de texte</Label>
                <Select value={textMode} onValueChange={setTextMode}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preserve">Préserver</SelectItem>
                    <SelectItem value="condense">Condenser</SelectItem>
                    <SelectItem value="generate">Générer (IA)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                Thème <span className="text-muted-foreground">(optionnel)</span>
              </Label>
              {templates.length > 0 ? (
                <Select
                  value={templates.includes(themeName) ? themeName : (themeName ? "__custom__" : "__none__")}
                  onValueChange={(v) => {
                    if (v === "__none__") setThemeName("");
                    else if (v === "__custom__") setThemeName(themeName || "");
                    else setThemeName(v);
                  }}
                >
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Choisir un template" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Aucun (thème par défaut Gamma)</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                    <SelectItem value="__custom__">Personnalisé…</SelectItem>
                  </SelectContent>
                </Select>
              ) : null}
              {(templates.length === 0 || (themeName && !templates.includes(themeName))) && (
                <Input
                  value={themeName}
                  onChange={(e) => setThemeName(e.target.value)}
                  placeholder="ex: Oasis, Atmosphere, Vortex…"
                  className="h-9 text-xs"
                />
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Instructions complémentaires <span className="text-muted-foreground">(optionnel)</span></Label>
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="ex: Garde un ton corporate, mets en avant les chiffres clés…"
                rows={3}
                className="text-xs resize-none"
              />
            </div>
          </div>
        )}

        {stage === "generating" && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <div className="text-sm text-muted-foreground text-center">{pollMessage}</div>
            <div className="text-[13px] text-muted-foreground">Cela peut prendre jusqu'à 2 minutes.</div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {stage === "config" && (
            <>
              <Button variant="ghost" size="sm" onClick={() => close(null)}>Annuler</Button>
              <Button size="sm" onClick={startGeneration}>
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Générer
              </Button>
            </>
          )}
          {stage === "generating" && (
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Retour
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
